import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getAppConfig, getDeepLink, DEFAULT_APP } from '../constants/mobileApps';
import './AccessCodeDisplay.css';

/**
 * Reusable Access Code Display Component
 *
 * Shows access code with QR code deeplink for success screens.
 * Used after payment completion or study enrollment.
 *
 * @param {object} props
 * @param {string} props.accessCode - The access code to display (e.g., 'ABC-123')
 * @param {string} [props.appId] - App identifier (defaults to DEFAULT_APP)
 * @param {string} [props.label] - Label above the code (default: 'YOUR ACCESS CODE')
 * @param {string} [props.qrInstruction] - Text above QR code
 * @param {boolean} [props.showQR] - Whether to show QR code (default: true)
 * @param {number} [props.qrSize] - QR code size in pixels (default: 160)
 * @param {string} [props.variant] - Visual variant: 'default' | 'gradient' | 'minimal'
 *
 * @example
 * // Basic usage
 * <AccessCodeDisplay accessCode="ABC-123" />
 *
 * @example
 * // Gradient style (for enrollment modal)
 * <AccessCodeDisplay
 *   accessCode="ABC-123"
 *   variant="gradient"
 *   label="YOUR ACCESS CODE"
 * />
 */
const AccessCodeDisplay = ({
    accessCode,
    appId = DEFAULT_APP,
    label = 'YOUR ACCESS CODE',
    qrInstruction,
    showQR = true,
    qrSize = 160,
    variant = 'default'
}) => {
    const config = getAppConfig(appId);
    const deepLink = getDeepLink(appId, accessCode);

    const defaultQrInstruction = `Scan to open in ${config.name} app:`;
    const displayQrInstruction = qrInstruction || defaultQrInstruction;

    return (
        <div className={`access-code-display access-code-display--${variant}`}>
            <div className="access-code-display__code-box">
                <div className="access-code-display__label">{label}</div>
                <div className="access-code-display__value">{accessCode}</div>
            </div>

            {showQR && (
                <div className="access-code-display__qr-section">
                    <p className="access-code-display__qr-instruction">
                        {displayQrInstruction}
                    </p>
                    <QRCodeSVG
                        value={deepLink}
                        size={qrSize}
                        level="M"
                        className="access-code-display__qr-code"
                    />
                </div>
            )}
        </div>
    );
};

export default AccessCodeDisplay;
